// A Google Meet code from what a user pastes, or from a Calendar link.
//
// Meet codes look like `abc-defg-hij`: ten lower-case letters in groups of
// 3-4-3. The import page accepts what people copy: the full link (with
// `?authuser=…`), the host without a scheme, the code in upper case, or the
// code without its dashes. Everything else is refused, `meet.google.com/
// lookup/<nickname>` too (a nickname, not a code).
//
// The code ends up inside a Google `filter` string (`space.meeting_code =
// "…"`), so this strict shape is the only thing that keeps a quote or a
// space out of it. Pure and browser-safe: `URL` and `URLSearchParams`, no
// regex lookbehind (Safari 14).

/** The canonical shape: `abc-defg-hij`. */
const CANONICAL = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/u;
/** What a user may type: the canonical shape, or the ten letters without dashes. */
const TYPED = /^(?:[a-z]{3}-[a-z]{4}-[a-z]{3}|[a-z]{10})$/u;

const MEET_HOST = "meet.google.com";

/** True for a canonical Meet code (`abc-defg-hij`, lower case). */
export const isMeetCode = (value: string): boolean => CANONICAL.test(value);

/** A typed code in its canonical form, or null. */
const canonicalCode = (raw: string): string | null => {
  const lower = raw.toLowerCase();
  if (!TYPED.test(lower)) return null;
  if (lower.includes("-")) return lower;
  return `${lower.slice(0, 3)}-${lower.slice(3, 7)}-${lower.slice(7)}`;
};

/** A parsed Meet link or code. */
export interface MeetInput {
  /** Canonical: `abc-defg-hij`. */
  readonly code: string;
  /**
   * The link's `authuser` (an e-mail or an account index), so the page can
   * say when the link was for another Google account than the connected one.
   * Null without one.
   */
  readonly authuser: string | null;
}

/**
 * The code (and `authuser`) of a `meet.google.com` URL, or null. Only that
 * host, only a code as the first path segment.
 */
export const meetCodeOfUrl = (value: string): MeetInput | null => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.hostname.toLowerCase() !== MEET_HOST) return null;
  const segments = url.pathname.split("/").filter((s) => s.length > 0);
  if (segments.length !== 1) return null;
  const code = canonicalCode(segments[0]!);
  if (code === null) return null;
  const authuser = url.searchParams.get("authuser");
  return { code, authuser: authuser !== null && authuser.length > 0 ? authuser : null };
};

/**
 * What a user pasted: a Meet link (with or without `https://`), or a code.
 * Null for anything else.
 */
export const parseMeetInput = (raw: string): MeetInput | null => {
  const value = raw.trim();
  if (value.length === 0 || value.length > 2048) return null;
  if (/^https?:\/\//iu.test(value)) return meetCodeOfUrl(value);
  if (value.toLowerCase().startsWith(`${MEET_HOST}/`)) return meetCodeOfUrl(`https://${value}`);
  const code = canonicalCode(value);
  return code === null ? null : { code, authuser: null };
};
